// URL builders — pure, testable. DOM mount logic added in Task 2.

const DEFAULT_HOST = 'worldcupcalendars.com';

export function buildIcsPath(codes: readonly string[]): string {
  return codes.map((c) => c.toLowerCase()).join(',');
}

export function buildWebcalUrl(codes: readonly string[], host: string = DEFAULT_HOST): string {
  if (codes.length === 0) throw new Error('At least one team is required');
  return 'webcal://' + host + '/cal/' + buildIcsPath(codes) + '.ics';
}

export function buildGoogleUrl(codes: readonly string[], host: string = DEFAULT_HOST): string {
  if (codes.length === 0) throw new Error('At least one team is required');
  const webcal = buildWebcalUrl(codes, host);
  return 'https://calendar.google.com/calendar/r?cid=' + webcal;
}

export function buildOutlookUrl(codes: readonly string[], host: string = DEFAULT_HOST): string {
  if (codes.length === 0) throw new Error('At least one team is required');
  const feedUrl = 'webcal://' + host + '/cal/' + buildIcsPath(codes) + '.ics';
  return 'https://outlook.live.com/calendar/0/addcalendar?url=' + encodeURIComponent(feedUrl) + '&name=' + encodeURIComponent('World Cup 2026');
}

import type { Selection } from './state.js';
import { showSection } from './router.js';
import { t, fill } from './i18n.js';
import fixtures from '../../data/fixtures.json';
import { track } from './analytics.js';

function countMatches(codes: readonly string[]): number {
  const set = new Set(codes);
  return fixtures.matches.filter(m => set.has(m.home) || set.has(m.away)).length;
}

const COPY_FEEDBACK_MS = 2000;

function showConfirmation(count: number): void {
  const actions = document.getElementById('subscribe-actions');
  const fallback = document.getElementById('copy-fallback');
  const confirm = document.getElementById('confirmation');
  const badge = document.getElementById('confirm-badge');
  if (!actions || !confirm) return;
  actions.hidden = true;
  if (fallback) fallback.hidden = true;
  if (badge) badge.textContent = fill(t('confirmBadge'), { n: count });
  confirm.hidden = false;
}

function resetActions(): void {
  const actions = document.getElementById('subscribe-actions');
  const confirm = document.getElementById('confirmation');
  const fallback = document.getElementById('copy-fallback');
  if (actions) actions.hidden = false;
  if (confirm) confirm.hidden = true;
  if (fallback) fallback.hidden = true;
  
  const copyBtn = document.getElementById('btn-copy');
  if (copyBtn) {
    const title = copyBtn.querySelector('.subscribe-btn__title');
    if (title) title.textContent = t('btnCopy');
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

function showCopyFallback(url: string): void {
  const fallback = document.getElementById('copy-fallback');
  const input = document.getElementById('copy-fallback-input') as HTMLInputElement | null;
  if (!fallback || !input) return;
  input.value = url;
  fallback.hidden = false;
  input.focus();
  input.select();
}

/**
 * Refresh URLs and counts every time the subscribe screen becomes visible
 * OR the selection changes while the screen is mounted.
 */
function refresh(selection: Selection): void {
  const codes = selection.codes();
  const count = codes.length;

  if (count === 0 && document.getElementById('subscribe')?.hidden === false) {
    showSection('landing');
    return;
  }

  const matchCount = countMatches(codes);
  const subhead = document.getElementById('subscribe-subhead');
  if (subhead) {
    subhead.textContent = fill(t('subscribeSubhead'), { n: count, m: matchCount });
  }

  const google = document.getElementById('btn-google') as HTMLButtonElement | null;
  const apple = document.getElementById('btn-apple') as HTMLAnchorElement | null;
  const outlook = document.getElementById('btn-outlook') as HTMLButtonElement | null;
  const copy = document.getElementById('btn-copy') as HTMLButtonElement | null;

  if (count === 0) {
    if (google) google.disabled = true;
    if (apple) { apple.removeAttribute('href'); apple.setAttribute('aria-disabled', 'true'); }
    if (outlook) outlook.disabled = true;
    if (copy) copy.disabled = true;
    return;
  }

  const webcal = buildWebcalUrl(codes);
  const googleUrl = buildGoogleUrl(codes);
  const outlookUrl = buildOutlookUrl(codes);

  if (google) { google.disabled = false; google.dataset.url = googleUrl; }
  if (apple) { apple.href = webcal; apple.removeAttribute('aria-disabled'); apple.dataset.url = webcal; }
  if (outlook) { outlook.disabled = false; outlook.dataset.url = outlookUrl; }
  if (copy) { copy.disabled = false; copy.dataset.url = webcal; }
}

export function mountSubscribeScreen(selection: Selection): void {
  const google = document.getElementById('btn-google') as HTMLButtonElement | null;
  const apple = document.getElementById('btn-apple') as HTMLAnchorElement | null;
  const outlook = document.getElementById('btn-outlook') as HTMLButtonElement | null;
  const copy = document.getElementById('btn-copy') as HTMLButtonElement | null;
  const refine = document.getElementById('refine-link') as HTMLAnchorElement | null;
  if (!google || !apple || !outlook || !copy || !refine) return;

  // Initial render + react to selection changes
  refresh(selection);
  selection.subscribe(() => refresh(selection));

  google.addEventListener('click', () => {
    const url = google.dataset.url;
    if (!url) return;
    track('calendar_added', { method: 'google', team_count: selection.size(), teams: selection.codes() });
    window.open(url, '_blank', 'noopener,noreferrer');
    showConfirmation(selection.size());
  });

  apple.addEventListener('click', (e) => {
    e.preventDefault();
    const url = apple.dataset.url;
    if (!url) return;
    track('calendar_added', { method: 'apple', team_count: selection.size(), teams: selection.codes() });
    window.location.href = url;
    showConfirmation(selection.size());
  });

  outlook.addEventListener('click', () => {
    const url = outlook.dataset.url;
    if (!url) return;
    track('calendar_added', { method: 'outlook', team_count: selection.size(), teams: selection.codes() });
    window.open(url, '_blank', 'noopener,noreferrer');
    showConfirmation(selection.size());
  });

  copy.addEventListener('click', async () => {
    const url = copy.dataset.url;
    if (!url) return;
    track('calendar_added', { method: 'copy', team_count: selection.size(), teams: selection.codes() });
    const ok = await copyToClipboard(url);
    if (ok) {
      const title = copy.querySelector('.subscribe-btn__title');
      if (title) {
        const original = title.textContent;
        title.textContent = t('copied');
        setTimeout(() => {
          if (title.textContent === t('copied')) title.textContent = original;
        }, COPY_FEEDBACK_MS);
      }
    } else {
      showCopyFallback(url);
    }
  });

  refine.addEventListener('click', (e) => {
    e.preventDefault();
    resetActions();
    showSection('select');
  });

}
